import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../api';
import './AuditHub.css';
import GeneratePortalModal from './GeneratePortalModal';
import { catClass } from '../../utils/tsc';

function barClass(pct) {
  if (pct >= 80) return 'bar-green';
  if (pct >= 50) return 'bar-amber';
  return 'bar-red';
}

function eviIconClass(type) {
  if (type === 'POLICY')        return 'evi-policy';
  if (type === 'ACCESS_REVIEW') return 'evi-review';
  return 'evi-test';
}

function eviIcon(type) {
  if (type === 'POLICY')        return 'fa-file-signature';
  if (type === 'ACCESS_REVIEW') return 'fa-user-check';
  return 'fa-flask';
}

function eviStatusClass(status) {
  if (status === 'COMPLETE') return 'evi-complete';
  if (status === 'FAILING')  return 'evi-failing';
  return 'evi-partial';
}

function EvidenceItem({ item }) {
  const { t } = useTranslation();
  return (
    <div className="hub-evidence-item">
      <div className={`hub-evi-icon ${eviIconClass(item.type)}`}>
        <i className={`fas ${eviIcon(item.type)}`} />
      </div>
      <div className="hub-evi-body">
        <div className="hub-evi-label">{item.label}</div>
        <div className="hub-evi-detail">{item.detail}</div>
      </div>
      <span className={`hub-evi-status ${eviStatusClass(item.status)}`}>
        {item.status === 'COMPLETE' ? t('AuditHub.statusComplete') :
         item.status === 'FAILING'  ? t('AuditHub.statusFailing')  : t('AuditHub.statusInProgress')}
      </span>
    </div>
  );
}

export default function AuditHub() {
  const { t } = useTranslation();
  const [categories,   setCategories]   = useState([]);
  const [selected,     setSelected]     = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [showPortal,   setShowPortal]   = useState(false);
  const [portals,      setPortals]      = useState([]);
  const [revoking,     setRevoking]     = useState(null);
  const [frameworks,   setFrameworks]   = useState([]);
  const [frameworkId,  setFrameworkId]  = useState(null);
  const [downloading,  setDownloading]  = useState(false);

  // Load the org's ENROLLED frameworks (those with scaffolded controls) for the selector.
  useEffect(() => {
    axiosInstance.get('/compliance/frameworks')
      .then(r => {
        const enrolled = (r.data || []).filter(f => (f.totalControls || 0) > 0);
        setFrameworks(enrolled);
        if (enrolled.length > 0) setFrameworkId(enrolled[0].frameworkId);
        else setLoading(false);
      })
      .catch(() => setLoading(false));

    axiosInstance.get('/auditor-portal')
      .then(r => setPortals(r.data))
      .catch(() => {});
  }, []);

  // Load the Audit Hub for the selected framework.
  useEffect(() => {
    if (frameworkId == null) return;
    setLoading(true);
    axiosInstance.get('/audit/hub', { params: { frameworkId } })
      .then(r => {
        setCategories(r.data || []);
        setSelected((r.data && r.data.length > 0) ? r.data[0] : null);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [frameworkId]);

  // Download the unified, branded Security & Compliance report (governance + ASM + iASM/BAS) as a PDF.
  const downloadUnifiedReport = async () => {
    if (!frameworkId) return;
    setDownloading(true);
    try {
      const res = await axiosInstance.get(`/reports/unified/${frameworkId}.pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'security-compliance-report.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      // eslint-disable-next-line no-alert
      window.alert(t('AuditHub.reportError', 'Could not generate the report. Please try again.'));
    } finally {
      setDownloading(false);
    }
  };

  const revokePortal = (token) => {
    setRevoking(token);
    axiosInstance.delete(`/auditor-portal/${token}`)
      .then(() => setPortals(prev => prev.map(p => p.token === token ? { ...p, active: false, revokedAt: new Date().toISOString() } : p)))
      .catch(() => {})
      .finally(() => setRevoking(null));
  };

  const activePortals = portals.filter(p => p.active);

  if (loading) {
    return (
      <div className="hub-page">
        <div className="hub-loading"><i className="fas fa-spinner fa-spin" /> {t('AuditHub.loading')}</div>
      </div>
    );
  }

  if (categories.length === 0) {
    return (
      <div className="hub-page">
        <div className="hub-header">
          <h2><i className="fas fa-shield-check" style={{ marginRight: 10, color: '#1d4ed8' }} />{t('AuditHub.title')}</h2>
          <p>{t('AuditHub.titleSubtitle')}</p>
        </div>
        <div className="hub-empty">
          <i className="fas fa-database" />
          <div style={{ fontWeight: 600, fontSize: 16, color: '#334155' }}>{t('AuditHub.noDataTitle')}</div>
          <div style={{ fontSize: 13, marginTop: 4 }}>{t('AuditHub.noDataHint')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="hub-page">
      {showPortal && (
        <GeneratePortalModal
          onClose={() => setShowPortal(false)}
          onGenerated={p => setPortals(prev => [p, ...prev])}
        />
      )}

      <div className="hub-header" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2><i className="fas fa-shield-check" style={{ marginRight: 10, color: '#1d4ed8' }} />{t('AuditHub.titleEvidenceLocker')}</h2>
          <p>{t('AuditHub.headerSubtitle')}</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {frameworks.length > 0 && (
            <select
              className="hub-fw-select"
              value={frameworkId || ''}
              onChange={e => setFrameworkId(Number(e.target.value))}
              style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontWeight: 600, color: '#334155' }}
              title={t('AuditHub.frameworkSelect', 'Framework')}
            >
              {frameworks.map(f => (
                <option key={f.frameworkId} value={f.frameworkId}>{f.frameworkName}</option>
              ))}
            </select>
          )}
          <button className="hub-portal-btn" onClick={downloadUnifiedReport} disabled={downloading || !frameworkId}>
            <i className={`fas ${downloading ? 'fa-spinner fa-spin' : 'fa-file-pdf'}`} />{' '}
            {downloading ? t('AuditHub.reportGenerating', 'Generating…') : t('AuditHub.downloadReportBtn', 'Download report')}
          </button>
          <button className="hub-portal-btn" onClick={() => setShowPortal(true)}>
            <i className="fas fa-link" /> {t('AuditHub.generatePortalBtn')}
          </button>
        </div>
      </div>

      {activePortals.length > 0 && (
        <div className="hub-portals-bar">
          <span className="hub-portals-label"><i className="fas fa-link" /> {t('AuditHub.activeAuditorLinks')}</span>
          {activePortals.map(p => (
            <div key={p.token} className="hub-portal-chip">
              <span className="hub-portal-cpa">{p.cpaName || t('AuditHub.auditorFallback')}</span>
              <span className="hub-portal-exp">{t('AuditHub.expires', { date: new Date(p.expiresAt).toLocaleDateString() })}</span>
              <button className="hub-portal-copy"
                      onClick={() => navigator.clipboard.writeText(`${window.location.origin}/#/auditor/${p.token}`)}>
                <i className="fas fa-copy" />
              </button>
              <button className="hub-portal-revoke" disabled={revoking === p.token}
                      onClick={() => revokePortal(p.token)}>
                {revoking === p.token ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-times" />}
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="hub-layout">
        {/* Left: control tree */}
        <div className="hub-tree">
          <div className="hub-tree-title">
            {(() => { const fw = frameworks.find(f => f.frameworkId === frameworkId); return fw ? `${fw.frameworkName} · ` : ''; })()}
            {t('AuditHub.tscControlCategories')}
          </div>
          {categories.map(cat => (
            <div key={cat.shorthand}
                 className={`hub-tree-item ${selected?.shorthand === cat.shorthand ? 'active' : ''} ${cat.inScope === false ? 'hub-oos' : ''}`}
                 onClick={() => setSelected(cat)}>
              <span className={`hub-tree-shorthand ${catClass(cat.shorthand)}`}>{cat.shorthand}</span>
              <div className="hub-tree-info">
                <div className="hub-tree-name" title={cat.name}>{cat.name}</div>
                {cat.inScope === false ? (
                  <div className="hub-tree-pct hub-oos-label">{t('AuditHub.notInScope')}</div>
                ) : (
                  <>
                    <div className="hub-tree-pct">{t('AuditHub.coverageTests', { coverage: cat.coverage, total: cat.total })}</div>
                    <div className="hub-tree-bar-wrap">
                      <div className={`hub-tree-bar ${barClass(cat.coverage)}`}
                           style={{ width: `${cat.coverage}%` }} />
                    </div>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Right: evidence panel */}
        {selected ? (
          <div className="hub-panel">
            <div className="hub-panel-header">
              <h3 className="hub-panel-title">
                <span className={`hub-tree-shorthand ${catClass(selected.shorthand)}`}
                      style={{ display: 'inline-block', marginRight: 10 }}>
                  {selected.shorthand}
                </span>
                {selected.name}
              </h3>
              <p className="hub-panel-sub">{t('AuditHub.panelSubtitle')}</p>
            </div>

            <div className="hub-stats">
              <div className="hub-stat-box">
                <div className={`hub-stat-val hub-val-pct`}>{selected.coverage}%</div>
                <div className="hub-stat-lbl">{t('AuditHub.statCoverage')}</div>
              </div>
              <div className="hub-stat-box">
                <div className="hub-stat-val hub-val-pass">{selected.passed}</div>
                <div className="hub-stat-lbl">{t('AuditHub.statPassing')}</div>
              </div>
              <div className="hub-stat-box">
                <div className="hub-stat-val hub-val-fail">{selected.failed}</div>
                <div className="hub-stat-lbl">{t('AuditHub.statFailing')}</div>
              </div>
              <div className="hub-stat-box">
                <div className="hub-stat-val hub-val-notrun">{selected.notRun}</div>
                <div className="hub-stat-lbl">{t('AuditHub.statNotRun')}</div>
              </div>
            </div>

            <div className="hub-evidence-list">
              <div style={{ fontSize: 12, fontWeight: 700, color: '#94a3b8',
                            textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 4 }}>
                {t('AuditHub.evidenceItems')}
              </div>
              {(selected.evidence || []).length === 0 ? (
                <div style={{ fontSize: 13, color: '#94a3b8', padding: '8px 0' }}>
                  {t('AuditHub.noEvidence')}
                </div>
              ) : (
                (selected.evidence || []).map((ev, i) => (
                  <EvidenceItem key={i} item={ev} />
                ))
              )}
            </div>
          </div>
        ) : (
          <div className="hub-panel">
            <div className="hub-empty">
              <i className="fas fa-hand-pointer" />
              <div>{t('AuditHub.selectCategory')}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
